import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Input,
  Switch,
  message,
  Upload,
  Typography,
  Progress,
} from 'antd';
import type { UploadProps } from 'antd/es/upload';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  StopOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import {
  useOperations,
  useDeleteOperation,
  useActivateOperation,
  useDeactivateOperation,
} from '@/hooks/useManufacturingPhase2';
import { Operation, OperationType, SkillLevel } from '@/services/manufacturing-phase2.service';
import api from '@/services/api.service';
import { postExcelImportWithRetry } from '@/utils/excelImportUpload';

const { Text } = Typography;

const OperationsMasterPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [isExcelBusy, setIsExcelBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  // API hooks
  const { data: operations = [], isLoading } = useOperations({ search: searchText, active_only: showActiveOnly });
  const deleteOperation = useDeleteOperation();
  const activateOperation = useActivateOperation();
  const deactivateOperation = useDeactivateOperation();

  const handleCreate = () => {
    navigate('/mfg/operations/new');
  };

  const handleEdit = (record: Operation) => {
    navigate(`/mfg/operations/${record.id}/edit`);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Delete Operation',
      content: 'Are you sure you want to delete this operation?',
      onOk: () => deleteOperation.mutate(id),
    });
  };

  const handleToggleActive = (record: Operation) => {
    if (record.is_active) {
      deactivateOperation.mutate(record.id);
    } else {
      activateOperation.mutate(record.id);
    }
  };

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/manufacturing/operations/excel/template', {
        responseType: 'blob',
      });
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'operations-import-template.xlsx'
      );
      message.success('Template downloaded');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(e?.response?.data?.message || e?.message || 'Download failed');
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/manufacturing/operations/excel/export', {
        responseType: 'blob',
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `operations-${dateStr}.xlsx`
      );
      message.success('Export downloaded');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(e?.response?.data?.message || e?.message || 'Export failed');
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleImportExcel: UploadProps['customRequest'] = async (options) => {
    const { file, onError, onSuccess } = options;
    const formData = new FormData();
    formData.append('file', file as File);
    setIsExcelBusy(true);
    setUploadPct(0);
    try {
      const res = await postExcelImportWithRetry(
        api.getClient(),
        '/manufacturing/operations/excel/import',
        formData,
        { onUploadProgress: (p) => setUploadPct(p) }
      );
      const body = res.data as {
        success?: boolean;
        message?: string;
        data?: { created: number; updated: number; errors?: string[] };
      };
      if (body?.success !== false) {
        const d = body?.data;
        const errList = d?.errors?.length ? ` (${d.errors.length} row message(s))` : '';
        message.success((body?.message || 'Import completed') + errList);
        if (d?.errors?.length) {
          Modal.warning({
            title: 'Import notes',
            width: 560,
            content: (
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {d.errors!.slice(0, 50).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
                {d.errors!.length > 50 ? <Text type="secondary">…and more</Text> : null}
              </div>
            ),
          });
        }
        await queryClient.invalidateQueries({ queryKey: ['operations'] });
        onSuccess?.(body, file as Blob);
      } else {
        throw new Error(body?.message || 'Import failed');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(e?.response?.data?.message || e?.message || 'Import failed');
      onError?.(err as Error);
    } finally {
      setIsExcelBusy(false);
      setUploadPct(null);
    }
  };

  const columns: ColumnsType<Operation> = [
    {
      title: 'Operation Code',
      dataIndex: 'operation_code',
      key: 'operation_code',
      width: 150,
      fixed: 'left',
    },
    {
      title: 'Operation Name',
      dataIndex: 'operation_name',
      key: 'operation_name',
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'operation_type',
      key: 'operation_type',
      width: 150,
      render: (type: OperationType) => {
        const colors: Record<OperationType, string> = {
          [OperationType.MACHINING]: 'blue',
          [OperationType.ASSEMBLY]: 'green',
          [OperationType.WELDING]: 'orange',
          [OperationType.PAINTING]: 'purple',
          [OperationType.QUALITY_CHECK]: 'gold',
          [OperationType.PACKING]: 'cyan',
          [OperationType.MATERIAL_HANDLING]: 'magenta',
          [OperationType.HEAT_TREATMENT]: 'red',
          [OperationType.SURFACE_TREATMENT]: 'lime',
          [OperationType.CUSTOM]: 'default',
        };
        return <Tag color={colors[type]}>{type}</Tag>;
      },
    },
    {
      title: 'Setup (hrs)',
      dataIndex: 'standard_setup_time_hours',
      key: 'standard_setup_time_hours',
      width: 120,
      align: 'right',
      render: (val: number) => Number(val).toFixed(2),
    },
    {
      title: 'Run (hrs/unit)',
      dataIndex: 'standard_run_time_per_unit_hours',
      key: 'standard_run_time_per_unit_hours',
      width: 120,
      align: 'right',
      render: (val: number) => Number(val).toFixed(4),
    },
    {
      title: 'Teardown (hrs)',
      dataIndex: 'standard_teardown_time_hours',
      key: 'standard_teardown_time_hours',
      width: 120,
      align: 'right',
      render: (val: number) => Number(val).toFixed(2),
    },
    {
      title: 'Per hr cost',
      dataIndex: 'per_hour_cost',
      key: 'per_hour_cost',
      width: 110,
      align: 'right',
      render: (val: number) => Number(val ?? 0).toFixed(2),
    },
    {
      title: 'Skill Level',
      dataIndex: 'skill_level_required',
      key: 'skill_level_required',
      width: 120,
      render: (level: SkillLevel) => {
        const colors: Record<SkillLevel, string> = {
          [SkillLevel.BASIC]: 'default',
          [SkillLevel.INTERMEDIATE]: 'blue',
          [SkillLevel.ADVANCED]: 'orange',
          [SkillLevel.EXPERT]: 'red',
        };
        return <Tag color={colors[level]}>{level}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            icon={record.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleActive(record)}
          >
            {record.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="Operations Master"
        extra={
          <Space>
            <Input
              placeholder="Search operations..."
              prefix={<SearchOutlined />}
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
            <Switch
              checkedChildren="Active Only"
              unCheckedChildren="Show All"
              checked={showActiveOnly}
              onChange={setShowActiveOnly}
            />
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate} loading={isExcelBusy}>
              Template
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExportExcel} loading={isExcelBusy}>
              Export Excel
            </Button>
            <Upload accept=".xlsx,.xls" showUploadList={false} customRequest={handleImportExcel}>
              <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                Import Excel
              </Button>
            </Upload>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Operation
            </Button>
          </Space>
        }
      >
        {uploadPct !== null && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Uploading import file…</Text>
            <Progress percent={uploadPct} size="small" />
          </div>
        )}
        <Table
          columns={columns}
          dataSource={operations}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1620 }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>
    </div>
  );
};

export default OperationsMasterPage;

